import { formatPeriodYoyValue, PERIOD_YOY_GRANULARITY, resolveTrendChartPeriodValue } from '../../../filters/lib/statisticsPeriodYoyHelpers';
import {
	normalizeTrendYearSeriesColors,
	resolveTrendChartPalette,
	resolveTrendYearSeriesColor,
	resolvePeriodComparePointColor,
} from '../../../common/lib/statisticsChartPalette';
import { formatPrimaryPhaseMetricValue } from '../../pipeline/lib/orderPrimaryPhaseMetric';
import {
	isHorizontalBarOrientation,
	resolveBarBorderRadius,
	resolveBarLabelPosition,
	CHART_BAR_ORIENTATION,
} from '../../shared/lib/barOrientation';
import {
	isTrendBarChart,
	TREND_CHART_TYPE,
	TREND_LINE_SHAPE,
	resolveCompareLineSmooth,
} from './trendChartConfig';
import {
	buildDimensionCategoryAxisLayout,
	buildMixedCompareCategoryXAxes,
	buildPeriodCompareCategoryAxisLayout,
	resolveHorizontalCompareBarSeriesStyle,
	resolveHorizontalBarGapSeriesProps,
	resolveVerticalCompareBarGapSeriesProps,
	resolveYearMonthsCompareBoundaryGap,
	resolveHorizontalCompareBarGridBottom,
	MIXED_COMPARE_BAR_X_AXIS_INDEX,
	MIXED_COMPARE_LINE_X_AXIS_INDEX,
	MIXED_COMPARE_LINE_Z,
	resolveMixedCompareBarSeriesExtra,
} from './trendChartAxisLayout';
import { buildMixedOrAxisCompareTooltip, resolveCompareSeriesPointValueAt } from './trendMixedChartHelpers';
import {
	buildCompareAxisPointer,
	buildCompareValueYAxis,
	buildCompareLineLegend,
	buildLinePointLabel,
	buildPeriodCompareYearLegendSeries,
	resolveTrendChartGridTop,
	resolvePeriodCompareCategoryLabel,
	resolvePeriodCompareLegendName,
	resolvePeriodCompareTooltipHeader,
} from './trendChartOptionShared';
import { resolveLineCompareMetricBindings, resolveLineCompareYAxisLabels, resolveTrendMetricAxisFormat } from './trendYAxisMetrics';
import {
	isMixedMetricSeriesTypes,
	METRIC_SERIES_TYPE,
	normalizeMetricSeriesTypes,
	resolveMetricSeriesIsBar,
} from '../../shared/lib/metricSeriesTypes';

const resolveMultiMetricChartLayout = ({ metrics = [], chartType, barOrientation, metricSeriesTypes = {} }) => {
	const normalizedSeriesTypes = normalizeMetricSeriesTypes(metrics, metricSeriesTypes, {
		defaultType: isTrendBarChart(chartType) ? METRIC_SERIES_TYPE.BAR : METRIC_SERIES_TYPE.LINE,
	});
	const isMixed = isMixedMetricSeriesTypes(normalizedSeriesTypes);
	const resolveSeriesIsBar = (metric) => resolveMetricSeriesIsBar(metric, normalizedSeriesTypes, chartType, isTrendBarChart);
	const allBar = metrics.length > 0 && metrics.every((metric) => resolveSeriesIsBar(metric));
	const horizontalBar = !isMixed && allBar && isHorizontalBarOrientation(barOrientation);
	const hasAnyBar = metrics.some((metric) => resolveSeriesIsBar(metric));
	return { normalizedSeriesTypes, isMixed, horizontalBar, resolveSeriesIsBar, hasAnyBar, allBar };
};

const METRIC_SERIES_COLORS = ['#6366f1', '#38bdf8', '#34d399', '#f59e0b', '#fb7185', '#a78bfa'];

const resolveMetricSeriesColor = (index, accentColor, isLight) => {
	const { yearLines } = resolveTrendChartPalette(isLight);
	return index === 0 ? accentColor : yearLines[index % yearLines.length] ?? METRIC_SERIES_COLORS[index % METRIC_SERIES_COLORS.length];
};

const normalizeCompareSeriesPointValue = (value) => {
	if (value == null) return 0;
	if (typeof value === 'object' && 'value' in value) return Number(value.value) || 0;
	return Number(value) || 0;
};

const normalizeCompareSeriesValues = (values = []) => values.map((value) => normalizeCompareSeriesPointValue(value));

const buildDualValueAxes = (chartTheme, bindings, seriesValuesByAxis, { formatAxisValue } = {}) => {
	const { useDualAxis, leftName, rightName } = resolveLineCompareYAxisLabels(bindings);
	const leftValues = seriesValuesByAxis[0] ?? [];
	const rightValues = seriesValuesByAxis[1] ?? [];
	const leftBinding = bindings.find((item) => item.yAxisIndex === 0) ?? bindings[0];
	const rightBinding = bindings.find((item) => item.yAxisIndex === 1);

	if (!useDualAxis) {
		const axisFormat = resolveTrendMetricAxisFormat(leftBinding);
		return [
			buildCompareValueYAxis(chartTheme, {
				...axisFormat,
				formatAxisValue: formatAxisValue ?? axisFormat.formatAxisValue,
				values: leftValues.length ? leftValues : rightValues,
			}),
		];
	}

	const leftFormat = resolveTrendMetricAxisFormat(leftBinding);
	const rightFormat = resolveTrendMetricAxisFormat(rightBinding);

	return [
		{
			...buildCompareValueYAxis(chartTheme, {
				...leftFormat,
				values: leftValues,
			}),
			position: 'left',
			name: leftName,
			nameTextStyle: { color: chartTheme.labelColor, fontSize: 9 },
		},
		{
			...buildCompareValueYAxis(chartTheme, {
				...rightFormat,
				formatAxisValue: formatAxisValue ?? rightFormat.formatAxisValue,
				values: rightValues,
			}),
			position: 'right',
			name: rightName,
			nameTextStyle: { color: chartTheme.labelColor, fontSize: 9 },
		},
	];
};

const buildMixedCompareAxisPointer = (chartTheme, chartType, metricSeriesTypes, linkCategoryAxes) => ({
	...buildCompareAxisPointer(chartTheme, chartType, metricSeriesTypes),
	...(linkCategoryAxes ? { link: [{ xAxisIndex: [MIXED_COMPARE_BAR_X_AXIS_INDEX, MIXED_COMPARE_LINE_X_AXIS_INDEX] }] } : {}),
});

const buildYearMonthsCompareLineSeriesItem = (def, baseItem, { xAxisIndex = 0 } = {}) => {
	const pointValues = normalizeCompareSeriesValues(def.values);
	const seriesData = pointValues.map((value) => ({ value }));
	return {
		...baseItem,
		connectNulls: false,
		data: seriesData,
		xAxisIndex,
		yAxisIndex: def.yAxisIndex,
		z: MIXED_COMPARE_LINE_Z,
		zlevel: 1,
		symbolSize: def.isAnchor ? 8 : 7,
		showSymbol: true,
		triggerLineEvent: true,
		lineStyle: {
			width: def.isAnchor ? 2.5 : 2,
			color: def.color,
			type: def.isAnchor ? 'solid' : 'dashed',
		},
		itemStyle: {
			color: def.color,
			borderColor: def.isAnchor ? '#fff' : '#fff',
			borderWidth: def.isAnchor ? 1.5 : 1,
		},
		emphasis: { focus: 'series', scale: def.isAnchor },
		...(baseItem.label?.show
			? {
					label: {
						...baseItem.label,
						formatter: (params) => {
							const raw = resolveCompareSeriesPointValueAt(pointValues, params?.dataIndex);
							if (raw == null) return '';
							return formatPrimaryPhaseMetricValue(raw, def.metric);
						},
					},
				}
			: {}),
	};
};

const buildHorizontalDualValueAxes = (chartTheme, bindings, seriesValuesByAxis) => {
	const { useDualAxis, leftName, rightName } = resolveLineCompareYAxisLabels(bindings);
	const bottomValues = seriesValuesByAxis[0] ?? [];
	const topValues = seriesValuesByAxis[1] ?? [];
	const leftBinding = bindings.find((item) => item.yAxisIndex === 0) ?? bindings[0];
	const rightBinding = bindings.find((item) => item.yAxisIndex === 1);

	if (!useDualAxis) {
		const axisFormat = resolveTrendMetricAxisFormat(leftBinding);
		return [
			buildCompareValueYAxis(chartTheme, {
				...axisFormat,
				values: bottomValues.length ? bottomValues : topValues,
			}),
		];
	}

	const leftFormat = resolveTrendMetricAxisFormat(leftBinding);
	const rightFormat = resolveTrendMetricAxisFormat(rightBinding);

	return [
		{
			...buildCompareValueYAxis(chartTheme, {
				...leftFormat,
				values: bottomValues,
			}),
			position: 'bottom',
			name: leftName,
			nameTextStyle: { color: chartTheme.labelColor, fontSize: 9 },
		},
		{
			...buildCompareValueYAxis(chartTheme, {
				...rightFormat,
				values: topValues,
			}),
			position: 'top',
			name: rightName,
			nameTextStyle: { color: chartTheme.labelColor, fontSize: 9 },
		},
	];
};

export const buildMetricSeriesItem = ({
	name,
	values,
	yAxisIndex = 0,
	xAxisIndex = 0,
	chartMetric,
	color,
	chartType,
	lineSmooth,
	showPointLabels,
	chartTheme,
	barBorderRadius,
	barLabelPosition,
	isBar,
	barMaxWidth = 28,
}) => {
	if (isBar) {
		return {
			name,
			type: 'bar',
			yAxisIndex,
			xAxisIndex,
			barMaxWidth,
			data: values,
			itemStyle: {
				...(color ? { color } : {}),
				borderRadius: barBorderRadius,
			},
			label: buildLinePointLabel({
				show: showPointLabels,
				chartTheme,
				color,
				formatValue: (value) => formatPrimaryPhaseMetricValue(value, chartMetric),
				position: barLabelPosition,
			}),
			emphasis: { focus: 'series' },
			animationDuration: 0,
			animationDurationUpdate: 0,
			animationEasing: 'cubicOut',
		};
	}

	return {
		name,
		type: 'line',
		yAxisIndex,
		xAxisIndex,
		smooth: lineSmooth,
		connectNulls: false,
		data: values,
		lineStyle: { width: 2.5, ...(color ? { color } : {}) },
		itemStyle: color ? { color, borderColor: color, borderWidth: 1 } : { borderWidth: 1 },
		label: buildLinePointLabel({
			show: showPointLabels,
			chartTheme,
			color,
			formatValue: (value) => formatPrimaryPhaseMetricValue(value, chartMetric),
		}),
		emphasis: { focus: 'series' },
		animationDuration: 0,
		animationDurationUpdate: 0,
		animationEasing: 'cubicOut',
	};
};

export const buildMultiMetricPeriodCompareLineOption = ({
	timeline = [],
	metrics = [],
	chartTheme,
	accentColor,
	showPointLabels = true,
	chartType = TREND_CHART_TYPE.LINE,
	lineShape = TREND_LINE_SHAPE.SMOOTH,
	barOrientation = CHART_BAR_ORIENTATION.VERTICAL,
	metricSeriesTypes = {},
	isLight = false,
	yearSeriesColors = {},
}) => {
	if (!timeline.length || !metrics.length) return null;

	const bindings = resolveLineCompareMetricBindings(metrics);
	const categories = timeline.map((point) => resolvePeriodCompareCategoryLabel(point) || point.axisLabel);
	const anchorIndex = timeline.findIndex((point) => point.kind === 'anchor');
	const { normalizedSeriesTypes, horizontalBar, resolveSeriesIsBar, hasAnyBar } = resolveMultiMetricChartLayout({
		metrics,
		chartType,
		barOrientation,
		metricSeriesTypes,
	});
	const lineSmooth = resolveCompareLineSmooth(lineShape);
	const barBorderRadius = resolveBarBorderRadius(horizontalBar);
	const barLabelPosition = resolveBarLabelPosition(horizontalBar);
	const { useDualAxis } = resolveLineCompareYAxisLabels(bindings);
	const categoryAxisLayout = !horizontalBar && hasAnyBar
		? buildPeriodCompareCategoryAxisLayout(categories, chartTheme, { anchorIndex, accentColor })
		: null;

	const customYearColors = normalizeTrendYearSeriesColors(yearSeriesColors);
	const useYearPointColors = Object.keys(customYearColors).length > 0;
	const { comparePoint: comparePointColor } = resolveTrendChartPalette(isLight);
	let compareColorIndex = 0;
	const pointColors = useYearPointColors
		? timeline.map((point) => {
				const isAnchor = point?.kind === 'anchor';
				const compareIndex = isAnchor ? 0 : compareColorIndex++;
				return resolvePeriodComparePointColor(point, {
					yearSeriesColors: customYearColors,
					accentColor,
					isLight,
					fallbackCompareColor: comparePointColor,
					compareIndex,
				});
			})
		: null;

	const seriesDefs = bindings.map((binding, index) => {
		const rawValues = timeline.map((point) =>
			resolveTrendChartPeriodValue(point.startDate, point.valuesByMetric?.[binding.metric] ?? point.value),
		);
		const metricColor = resolveMetricSeriesColor(index, accentColor, isLight);
		const values =
			pointColors != null
				? rawValues.map((value, pointIndex) => ({
						value,
						itemStyle: {
							color: pointColors[pointIndex],
							...(resolveSeriesIsBar(binding.metric) ? { borderRadius: barBorderRadius } : {}),
						},
						label: { color: pointColors[pointIndex] },
					}))
				: rawValues;
		return {
			...binding,
			name: binding.label,
			values,
			color: pointColors != null ? null : metricColor,
		};
	});

	const seriesValuesByAxis = {
		0: seriesDefs.filter((item) => item.yAxisIndex === 0).map((item) => item.values),
		1: seriesDefs.filter((item) => item.yAxisIndex === 1).map((item) => item.values),
	};

	const yearLegendNames = useYearPointColors ? timeline.map((point) => resolvePeriodCompareLegendName(point)) : null;
	const legend = buildCompareLineLegend(chartTheme, {
		data: yearLegendNames ?? seriesDefs.map((item) => item.name),
		itemCount: (yearLegendNames ?? seriesDefs).length,
	});
	if (yearLegendNames) legend.selectedMode = false;
	const yearLegendSeries = useYearPointColors ? buildPeriodCompareYearLegendSeries(timeline, pointColors) : [];
	const legendItemCount = (yearLegendNames ?? seriesDefs).length;

	const tooltipFormatter = (items) => {
		if (!items?.length) return '';
		const point = timeline[items[0].dataIndex];
		if (!point) return items[0].axisValue ?? items[0].name ?? '';
		const lines = [resolvePeriodCompareTooltipHeader(point)];
		if (point.periodLabel && point.periodLabel !== point.axisLabel && point.kind !== 'anchor') {
			lines.push(point.periodLabel);
		}
		for (const item of items) {
			const binding = seriesDefs.find((def) => def.name === item.seriesName);
			if (!binding) continue;
			const rawValue = resolveCompareSeriesPointValueAt(binding.values, item.dataIndex);
			lines.push(`${item.marker}${item.seriesName} ${formatPrimaryPhaseMetricValue(rawValue, binding.metric ?? 'count')}`);
		}
		return lines.join('<br/>');
	};

	if (horizontalBar) {
		const valueAxes = buildHorizontalDualValueAxes(chartTheme, bindings, seriesValuesByAxis);
		const barStyle = resolveHorizontalCompareBarSeriesStyle(seriesDefs.length);
		return {
			backgroundColor: 'transparent',
			animationDurationUpdate: 0,
			legend,
			grid: {
				left: 8,
				right: useDualAxis ? 36 : 24,
				top: resolveTrendChartGridTop({ showPointLabels, legendItemCount }),
				bottom: resolveHorizontalCompareBarGridBottom(useDualAxis),
				containLabel: true,
			},
			tooltip: {
				...chartTheme.tooltip,
				trigger: 'axis',
				axisPointer: buildCompareAxisPointer(chartTheme, chartType, normalizedSeriesTypes),
				formatter: tooltipFormatter,
			},
			xAxis: valueAxes,
			yAxis: {
				type: 'category',
				data: categories,
				inverse: true,
				...chartTheme.axis,
				splitLine: { show: false },
				axisLabel: {
					...chartTheme.axis.axisLabel,
					color: chartTheme.tooltip?.textStyle?.color ?? chartTheme.axis.axisLabel?.color,
					fontSize: 11,
					fontWeight: 600,
					interval: 0,
				},
			},
			series: [
				...yearLegendSeries,
				...seriesDefs.map((def, index) => ({
					...buildMetricSeriesItem({
						name: def.name,
						values: def.values,
						yAxisIndex: 0,
						xAxisIndex: def.yAxisIndex,
						chartMetric: def.metric,
						color: def.color,
						chartType,
						lineSmooth,
						showPointLabels,
						chartTheme,
						barBorderRadius,
						barLabelPosition,
						isBar: resolveSeriesIsBar(def.metric),
						barMaxWidth: barStyle.barMaxWidth,
					}),
					...(resolveHorizontalBarGapSeriesProps(seriesDefs, index, barStyle) ?? null),
				})),
			],
		};
	}

	const valueAxes = buildDualValueAxes(chartTheme, bindings, seriesValuesByAxis);
	const useSplitCategoryXAxis = hasAnyBar && !horizontalBar;
	const categoryXAxis = useSplitCategoryXAxis
		? buildMixedCompareCategoryXAxes(categories, chartTheme, {
				barAxisExtra: {
					axisLabel: categoryAxisLayout?.axisLabel ?? {
						...chartTheme.axis.axisLabel,
						fontSize: 9,
						interval: 0,
						hideOverlap: false,
						showMinLabel: true,
						showMaxLabel: true,
						margin: 10,
						overflow: 'truncate',
						formatter: (value, index) => {
							const point = timeline[index];
							if (point?.kind === 'anchor') return `{anchor|${value}}\n{tag|基准}`;
							return value;
						},
						rich: {
							anchor: { fontWeight: 700, fontSize: 9, color: accentColor, lineHeight: 14 },
							tag: { fontSize: 8, color: accentColor, lineHeight: 12 },
						},
					},
				},
			})
		: {
				type: 'category',
				data: categories,
				boundaryGap: hasAnyBar,
				...chartTheme.axis,
				axisLabel: categoryAxisLayout?.axisLabel ?? {
					...chartTheme.axis.axisLabel,
					fontSize: 9,
					interval: 0,
					hideOverlap: false,
					showMinLabel: true,
					showMaxLabel: true,
					margin: 10,
					overflow: 'truncate',
					formatter: (value, index) => {
						const point = timeline[index];
						if (point?.kind === 'anchor') return `{anchor|${value}}\n{tag|基准}`;
						return value;
					},
					rich: {
						anchor: { fontWeight: 700, fontSize: 9, color: accentColor, lineHeight: 14 },
						tag: { fontSize: 8, color: accentColor, lineHeight: 12 },
					},
				},
				axisTick: { alignWithLabel: true },
				splitLine: { show: false },
			};
	return {
		backgroundColor: 'transparent',
		animationDurationUpdate: 0,
		legend,
		grid: {
			left: useDualAxis ? 42 : 6,
			right: useDualAxis ? 42 : 6,
			top: resolveTrendChartGridTop({ showPointLabels, legendItemCount }),
			bottom: categoryAxisLayout?.gridBottom ?? (anchorIndex >= 0 ? 12 : 4),
			containLabel: true,
		},
		...(categoryAxisLayout?.dataZoom?.length ? { dataZoom: categoryAxisLayout.dataZoom } : {}),
		tooltip: buildMixedOrAxisCompareTooltip({
			chartTheme,
			useItemTrigger: useSplitCategoryXAxis,
			axisPointer: buildMixedCompareAxisPointer(chartTheme, chartType, normalizedSeriesTypes, useSplitCategoryXAxis),
			axisFormatter: tooltipFormatter,
			seriesDefs,
			formatSeriesValue: (def, rawValue) => formatPrimaryPhaseMetricValue(rawValue, def?.metric ?? 'count'),
		}),
		xAxis: categoryXAxis,
		yAxis: valueAxes.length === 1 ? valueAxes[0] : valueAxes,
		series: [
			...yearLegendSeries,
			...seriesDefs.map((def, index) => {
				const seriesIsBar = resolveSeriesIsBar(def.metric);
				const lineXAxisIndex = useSplitCategoryXAxis ? MIXED_COMPARE_LINE_X_AXIS_INDEX : 0;
				const seriesItem = buildMetricSeriesItem({
					name: def.name,
					values: def.values,
					yAxisIndex: def.yAxisIndex,
					xAxisIndex: seriesIsBar ? MIXED_COMPARE_BAR_X_AXIS_INDEX : lineXAxisIndex,
					chartMetric: def.metric,
					color: def.color,
					chartType,
					lineSmooth,
					showPointLabels,
					chartTheme,
					barBorderRadius,
					barLabelPosition,
					isBar: seriesIsBar,
				});
				if (seriesIsBar) {
					Object.assign(seriesItem, resolveVerticalCompareBarGapSeriesProps(index), resolveMixedCompareBarSeriesExtra());
				} else if (useSplitCategoryXAxis) {
					Object.assign(seriesItem, { z: MIXED_COMPARE_LINE_Z, zlevel: 1, triggerLineEvent: true });
				}
				if (!seriesIsBar && index === 0 && anchorIndex >= 0) {
					const anchorValue = resolveCompareSeriesPointValueAt(def.values, anchorIndex);
					seriesItem.markPoint = {
						symbol: 'pin',
						symbolSize: 36,
						symbolOffset: [0, '-35%'],
						data: [{ name: '基准', coord: [anchorIndex, anchorValue], value: '基准' }],
						label: { fontSize: 8, color: '#fff', fontWeight: 700 },
						itemStyle: { color: accentColor },
					};
				}
				return seriesItem;
			}),
		],
	};
};

export const buildMultiMetricYearMonthsCompareLineOption = ({
	yearMonthsSeriesByMetric = {},
	metrics = [],
	granularity = PERIOD_YOY_GRANULARITY.MONTH,
	chartTheme,
	accentColor,
	showPointLabels = true,
	chartType = TREND_CHART_TYPE.LINE,
	lineShape = TREND_LINE_SHAPE.SMOOTH,
	barOrientation = CHART_BAR_ORIENTATION.VERTICAL,
	metricSeriesTypes = {},
	isLight = false,
	yearSeriesColors = {},
}) => {
	const bindings = resolveLineCompareMetricBindings(metrics);
	const firstSeries = bindings.map((binding) => yearMonthsSeriesByMetric[binding.metric]).find(Boolean);
	if (!firstSeries?.series?.length) return null;

	const { axisLabels, anchorYear } = firstSeries;
	const { normalizedSeriesTypes, horizontalBar, resolveSeriesIsBar, hasAnyBar } = resolveMultiMetricChartLayout({
		metrics,
		chartType,
		barOrientation,
		metricSeriesTypes,
	});
	const lineSmooth = resolveCompareLineSmooth(lineShape);
	const barBorderRadius = resolveBarBorderRadius(horizontalBar);
	const barLabelPosition = resolveBarLabelPosition(horizontalBar);
	const { useDualAxis } = resolveLineCompareYAxisLabels(bindings);

	const yearColorCache = {};
	let compareColorIndex = 0;
	const resolveYearColor = (year, isAnchor) => {
		const key = String(year);
		if (yearColorCache[key]) return yearColorCache[key];
		const fallbackIndex = isAnchor ? 0 : compareColorIndex++;
		const color = resolveTrendYearSeriesColor({
			year,
			isAnchor,
			colors: yearSeriesColors,
			accentColor,
			isLight,
			fallbackIndex,
		});
		yearColorCache[key] = color;
		return color;
	};

	const seriesDefs = [];
	for (const binding of bindings) {
		const metricSeries = yearMonthsSeriesByMetric[binding.metric];
		if (!metricSeries?.series?.length) continue;
		const sortedSeries = [...metricSeries.series].sort((left, right) => {
			if (left.year === anchorYear) return -1;
			if (right.year === anchorYear) return 1;
			return left.year - right.year;
		});
		for (const item of sortedSeries) {
			const isAnchor = item.year === anchorYear;
			const lineColor = resolveYearColor(item.year, isAnchor);
			const name =
				bindings.length > 1
					? `${item.year}年 · ${binding.label}${isAnchor ? '（基准）' : ''}`
					: isAnchor
						? `${item.year}年（基准）`
						: `${item.year}年`;
			seriesDefs.push({
				...binding,
				name,
				values: item.values,
				color: lineColor,
				isAnchor,
			});
		}
	}

	if (!seriesDefs.length) return null;

	const rightBinding = bindings.find((binding) => binding.yAxisIndex === 1) ?? bindings.find((binding) => binding.currencyMetric);
	const formatAxisValue =
		rightBinding?.currencyMetric && rightBinding.metric
			? yearMonthsSeriesByMetric[rightBinding.metric]?.formatAxisValue
			: undefined;

	const seriesValuesByAxis = {
		0: seriesDefs.filter((item) => item.yAxisIndex === 0).map((item) => item.values),
		1: seriesDefs.filter((item) => item.yAxisIndex === 1).map((item) => item.values),
	};

	const legend = buildCompareLineLegend(chartTheme, {
		data: seriesDefs.map((item) => item.name),
		itemCount: seriesDefs.length,
	});

	const tooltipFormatter = (items) => {
		if (!items?.length) return '';
		const lines = [items[0].axisValue ?? items[0].name];
		for (const item of items) {
			const def = seriesDefs.find((entry) => entry.name === item.seriesName);
			const rawValue = resolveCompareSeriesPointValueAt(def?.values, item.dataIndex);
			if (rawValue == null) continue;
			lines.push(`${item.marker}${item.seriesName}：${formatPeriodYoyValue(rawValue, def?.valueField)}`);
		}
		return lines.join('<br/>');
	};

	if (horizontalBar) {
		const valueAxes = buildHorizontalDualValueAxes(chartTheme, bindings, seriesValuesByAxis);
		const barStyle = resolveHorizontalCompareBarSeriesStyle(seriesDefs.length);
		return {
			backgroundColor: 'transparent',
			animationDurationUpdate: 0,
			legend,
			grid: {
				left: 8,
				right: useDualAxis ? 36 : 24,
				top: resolveTrendChartGridTop({ showPointLabels, legendItemCount: seriesDefs.length }),
				bottom: resolveHorizontalCompareBarGridBottom(useDualAxis),
				containLabel: true,
			},
			tooltip: {
				...chartTheme.tooltip,
				trigger: 'axis',
				axisPointer: buildCompareAxisPointer(chartTheme, chartType, normalizedSeriesTypes),
				formatter: tooltipFormatter,
			},
			xAxis: valueAxes,
			yAxis: {
				type: 'category',
				data: axisLabels,
				inverse: true,
				...chartTheme.axis,
				splitLine: { show: false },
				axisLabel: { ...chartTheme.axis.axisLabel, fontSize: 9, interval: 0 },
			},
			series: seriesDefs.map((def, index) => ({
				...buildMetricSeriesItem({
					name: def.name,
					values: def.values,
					yAxisIndex: 0,
					xAxisIndex: def.yAxisIndex,
					chartMetric: def.metric,
					color: def.color,
					chartType,
					lineSmooth,
					showPointLabels,
					chartTheme,
					barBorderRadius,
					barLabelPosition,
					isBar: resolveSeriesIsBar(def.metric),
					barMaxWidth: barStyle.barMaxWidth,
				}),
				...resolveMixedCompareBarSeriesExtra(),
				...(resolveHorizontalBarGapSeriesProps(seriesDefs, index, barStyle) ?? null),
			})),
		};
	}

	const valueAxes = buildDualValueAxes(chartTheme, bindings, seriesValuesByAxis, { formatAxisValue });
	const useSplitCategoryXAxis = hasAnyBar && !horizontalBar;
	const categoryXAxis = useSplitCategoryXAxis
		? buildMixedCompareCategoryXAxes(axisLabels, chartTheme, {
				barAxisExtra: {
					axisLabel: { ...chartTheme.axis.axisLabel, fontSize: 9, interval: 0 },
				},
			})
		: {
				type: 'category',
				data: axisLabels,
				boundaryGap: resolveYearMonthsCompareBoundaryGap({ isBar: hasAnyBar, horizontalBar, granularity }),
				...chartTheme.axis,
				axisLabel: { ...chartTheme.axis.axisLabel, fontSize: 9, interval: 0 },
				...(hasAnyBar ? { axisTick: { alignWithLabel: true }, splitLine: { show: false } } : {}),
			};
	return {
		backgroundColor: 'transparent',
		animationDurationUpdate: 0,
		legend,
		grid: {
			left: useDualAxis ? 42 : 8,
			right: useDualAxis ? 42 : 8,
			top: resolveTrendChartGridTop({ showPointLabels, legendItemCount: seriesDefs.length }),
			bottom: 4,
			containLabel: true,
		},
		tooltip: buildMixedOrAxisCompareTooltip({
			chartTheme,
			useItemTrigger: useSplitCategoryXAxis,
			axisPointer: buildMixedCompareAxisPointer(chartTheme, chartType, normalizedSeriesTypes, useSplitCategoryXAxis),
			axisFormatter: tooltipFormatter,
			seriesDefs,
			formatSeriesValue: (def, rawValue) => formatPeriodYoyValue(rawValue, def?.valueField),
		}),
		xAxis: categoryXAxis,
		yAxis: valueAxes.length === 1 ? valueAxes[0] : valueAxes,
		series: seriesDefs.map((def, index) => {
			const seriesIsBar = resolveSeriesIsBar(def.metric);
			const lineXAxisIndex = useSplitCategoryXAxis ? MIXED_COMPARE_LINE_X_AXIS_INDEX : 0;
			if (seriesIsBar) {
				return {
					...buildMetricSeriesItem({
						name: def.name,
						values: def.values,
						yAxisIndex: def.yAxisIndex,
						xAxisIndex: MIXED_COMPARE_BAR_X_AXIS_INDEX,
						chartMetric: def.metric,
						color: def.color,
						chartType,
						lineSmooth,
						showPointLabels,
						chartTheme,
						barBorderRadius,
						barLabelPosition,
						isBar: true,
					}),
					...resolveMixedCompareBarSeriesExtra(),
					...resolveVerticalCompareBarGapSeriesProps(index),
				};
			}
			return buildYearMonthsCompareLineSeriesItem(
				def,
				buildMetricSeriesItem({
					name: def.name,
					values: def.values,
					yAxisIndex: def.yAxisIndex,
					xAxisIndex: lineXAxisIndex,
					chartMetric: def.metric,
					color: def.color,
					chartType,
					lineSmooth,
					showPointLabels,
					chartTheme,
					barBorderRadius,
					barLabelPosition,
					isBar: false,
				}),
				{ xAxisIndex: lineXAxisIndex },
			);
		}),
	};
};

export const buildMultiMetricDimensionAxisSnapshotLineOption = ({
	axisLabels = [],
	seriesByMetric = {},
	metrics = [],
	fieldLabel = '',
	chartTheme,
	accentColor,
	showPointLabels = true,
	dimensionAxisMode = '',
	chartType = TREND_CHART_TYPE.LINE,
	lineShape = TREND_LINE_SHAPE.SMOOTH,
	barOrientation = CHART_BAR_ORIENTATION.VERTICAL,
	metricSeriesTypes = {},
	isLight = false,
}) => {
	const bindings = resolveLineCompareMetricBindings(metrics);
	if (!axisLabels.length || !bindings.length) return null;

	const { normalizedSeriesTypes, horizontalBar, resolveSeriesIsBar, hasAnyBar } = resolveMultiMetricChartLayout({
		metrics,
		chartType,
		barOrientation,
		metricSeriesTypes,
	});
	const categoryAxis = buildDimensionCategoryAxisLayout(axisLabels, dimensionAxisMode, chartTheme, {
		categoryOnY: horizontalBar,
	});
	const lineSmooth = resolveCompareLineSmooth(lineShape);
	const barBorderRadius = resolveBarBorderRadius(horizontalBar);
	const barLabelPosition = resolveBarLabelPosition(horizontalBar);
	const { useDualAxis } = resolveLineCompareYAxisLabels(bindings);

	const seriesDefs = bindings
		.map((binding, index) => ({
			...binding,
			name: binding.label,
			values: seriesByMetric[binding.metric]?.values ?? [],
			color: resolveMetricSeriesColor(index, accentColor, isLight),
		}))
		.filter((item) => item.values.length);

	if (!seriesDefs.length) return null;

	const seriesValuesByAxis = {
		0: seriesDefs.filter((item) => item.yAxisIndex === 0).map((item) => item.values),
		1: seriesDefs.filter((item) => item.yAxisIndex === 1).map((item) => item.values),
	};

	const useSplitCategoryXAxis = hasAnyBar && !horizontalBar;
	const categoryAxisConfig = horizontalBar
		? {
				type: 'category',
				data: axisLabels,
				boundaryGap: true,
				inverse: true,
				...chartTheme.axis,
				splitLine: { show: false },
				axisLabel: {
					...chartTheme.axis.axisLabel,
					...categoryAxis.axisLabel,
				},
			}
		: useSplitCategoryXAxis
			? buildMixedCompareCategoryXAxes(axisLabels, chartTheme, {
					barAxisExtra: {
						axisLabel: {
							...chartTheme.axis.axisLabel,
							...categoryAxis.axisLabel,
						},
					},
				})
			: {
					type: 'category',
					data: axisLabels,
					boundaryGap: true,
					...chartTheme.axis,
					splitLine: { show: false },
					axisLabel: {
						...chartTheme.axis.axisLabel,
						...categoryAxis.axisLabel,
					},
				};

	const legend = buildCompareLineLegend(chartTheme, { data: seriesDefs.map((item) => item.name) });

	const valueAxes = horizontalBar
		? buildHorizontalDualValueAxes(chartTheme, bindings, seriesValuesByAxis)
		: buildDualValueAxes(chartTheme, bindings, seriesValuesByAxis);
	const barStyle = horizontalBar ? resolveHorizontalCompareBarSeriesStyle(seriesDefs.length) : null;

	return {
		backgroundColor: 'transparent',
		animationDurationUpdate: 0,
		legend,
		grid: {
			left: categoryAxis.gridLeft ?? (useDualAxis ? 42 : 8),
			right: useDualAxis ? 42 : 8,
			top: resolveTrendChartGridTop({ showPointLabels, legendItemCount: seriesDefs.length }),
			bottom: categoryAxis.gridBottom,
			containLabel: true,
		},
		...(categoryAxis.dataZoom.length ? { dataZoom: categoryAxis.dataZoom } : {}),
		tooltip: buildMixedOrAxisCompareTooltip({
			chartTheme,
			useItemTrigger: useSplitCategoryXAxis,
			axisPointer: buildMixedCompareAxisPointer(chartTheme, chartType, normalizedSeriesTypes, useSplitCategoryXAxis),
			axisFormatter: (items) => {
				if (!items?.length) return '';
				const lines = [items[0].name ?? items[0].axisValue];
				for (const item of items) {
					const def = seriesDefs.find((entry) => entry.name === item.seriesName);
					const rawValue = resolveCompareSeriesPointValueAt(def?.values, item.dataIndex);
					lines.push(`${item.marker}${item.seriesName} ${formatPrimaryPhaseMetricValue(rawValue, def?.metric ?? 'count')}`);
				}
				return lines.join('<br/>');
			},
			seriesDefs,
			formatSeriesValue: (def, rawValue) => formatPrimaryPhaseMetricValue(rawValue, def?.metric ?? 'count'),
		}),
		xAxis: horizontalBar ? valueAxes : categoryAxisConfig,
		yAxis: horizontalBar ? categoryAxisConfig : valueAxes.length === 1 ? valueAxes[0] : valueAxes,
		series: seriesDefs.map((def, index) => {
			const seriesIsBar = resolveSeriesIsBar(def.metric);
			const lineXAxisIndex = useSplitCategoryXAxis ? MIXED_COMPARE_LINE_X_AXIS_INDEX : 0;
			return {
				...buildMetricSeriesItem({
					name: fieldLabel && seriesDefs.length === 1 ? fieldLabel : def.name,
					values: def.values,
					yAxisIndex: horizontalBar ? 0 : def.yAxisIndex,
					xAxisIndex: horizontalBar ? def.yAxisIndex : seriesIsBar ? MIXED_COMPARE_BAR_X_AXIS_INDEX : lineXAxisIndex,
					chartMetric: def.metric,
					color: def.color,
					chartType,
					lineSmooth,
					showPointLabels,
					chartTheme,
					barBorderRadius,
					barLabelPosition,
					isBar: seriesIsBar,
					barMaxWidth: barStyle?.barMaxWidth,
				}),
				...(seriesIsBar
					? {
							...(horizontalBar
								? resolveHorizontalBarGapSeriesProps(seriesDefs, index, barStyle)
								: resolveVerticalCompareBarGapSeriesProps(index)),
							...resolveMixedCompareBarSeriesExtra(),
						}
					: useSplitCategoryXAxis
						? { z: MIXED_COMPARE_LINE_Z, zlevel: 1, triggerLineEvent: true }
						: null),
			};
		}),
	};
};

export const buildMultiMetricDimensionCompareLineOption = ({
	dimensionSeriesByMetric = {},
	metrics = [],
	chartTheme,
	showPointLabels = true,
	dimensionAxisMode = '',
	chartType = TREND_CHART_TYPE.LINE,
	lineShape = TREND_LINE_SHAPE.SMOOTH,
	barOrientation = CHART_BAR_ORIENTATION.VERTICAL,
	metricSeriesTypes = {},
	isLight = false,
	accentColor,
}) => {
	const bindings = resolveLineCompareMetricBindings(metrics);
	const firstSeries = bindings.map((binding) => dimensionSeriesByMetric[binding.metric]).find(Boolean);
	if (!firstSeries?.series?.length) return null;

	const { axisLabels } = firstSeries;
	const { normalizedSeriesTypes, horizontalBar, resolveSeriesIsBar, hasAnyBar } = resolveMultiMetricChartLayout({
		metrics,
		chartType,
		barOrientation,
		metricSeriesTypes,
	});
	const categoryAxis = buildDimensionCategoryAxisLayout(axisLabels, dimensionAxisMode, chartTheme, {
		categoryOnY: horizontalBar,
	});
	const lineSmooth = resolveCompareLineSmooth(lineShape);
	const barBorderRadius = resolveBarBorderRadius(horizontalBar);
	const barLabelPosition = resolveBarLabelPosition(horizontalBar);
	const { useDualAxis } = resolveLineCompareYAxisLabels(bindings);
	const { yearLines: yearLineColors } = resolveTrendChartPalette(isLight);

	let colorIndex = 0;
	const seriesDefs = [];
	for (const binding of bindings) {
		const metricSeries = dimensionSeriesByMetric[binding.metric];
		if (!metricSeries?.series?.length) continue;
		for (const item of metricSeries.series) {
			const lineColor = yearLineColors[colorIndex++ % yearLineColors.length];
			seriesDefs.push({
				...binding,
				name: bindings.length > 1 ? `${item.label} · ${binding.label}` : item.label,
				values: item.values,
				color: lineColor,
			});
		}
	}

	if (!seriesDefs.length) return null;

	const seriesValuesByAxis = {
		0: seriesDefs.filter((item) => item.yAxisIndex === 0).map((item) => item.values),
		1: seriesDefs.filter((item) => item.yAxisIndex === 1).map((item) => item.values),
	};

	const useSplitCategoryXAxis = hasAnyBar && !horizontalBar;
	const categoryAxisConfig = horizontalBar
		? {
				type: 'category',
				data: axisLabels,
				boundaryGap: true,
				inverse: true,
				...chartTheme.axis,
				splitLine: { show: false },
				axisLabel: { ...chartTheme.axis.axisLabel, ...categoryAxis.axisLabel },
			}
		: useSplitCategoryXAxis
			? buildMixedCompareCategoryXAxes(axisLabels, chartTheme, {
					barAxisExtra: {
						axisLabel: { ...chartTheme.axis.axisLabel, ...categoryAxis.axisLabel },
					},
				})
			: {
					type: 'category',
					data: axisLabels,
					boundaryGap: true,
					...chartTheme.axis,
					splitLine: { show: false },
					axisLabel: { ...chartTheme.axis.axisLabel, ...categoryAxis.axisLabel },
				};

	const valueAxes = horizontalBar
		? buildHorizontalDualValueAxes(chartTheme, bindings, seriesValuesByAxis)
		: buildDualValueAxes(chartTheme, bindings, seriesValuesByAxis);
	const barStyle = horizontalBar ? resolveHorizontalCompareBarSeriesStyle(seriesDefs.length) : null;

	return {
		backgroundColor: 'transparent',
		animationDurationUpdate: 0,
		legend: buildCompareLineLegend(chartTheme, { itemCount: seriesDefs.length }),
		grid: {
			left: categoryAxis.gridLeft ?? (useDualAxis ? 42 : 8),
			right: useDualAxis ? 42 : 8,
			top: resolveTrendChartGridTop({ showPointLabels, legendItemCount: seriesDefs.length }),
			bottom: categoryAxis.gridBottom,
			containLabel: true,
		},
		...(categoryAxis.dataZoom.length ? { dataZoom: categoryAxis.dataZoom } : {}),
		tooltip: buildMixedOrAxisCompareTooltip({
			chartTheme,
			useItemTrigger: useSplitCategoryXAxis,
			axisPointer: buildMixedCompareAxisPointer(chartTheme, chartType, normalizedSeriesTypes, useSplitCategoryXAxis),
			axisFormatter: (items) => {
				if (!items?.length) return '';
				const lines = [items[0].name ?? items[0].axisValue];
				for (const item of items) {
					const def = seriesDefs.find((entry) => entry.name === item.seriesName);
					const rawValue = resolveCompareSeriesPointValueAt(def?.values, item.dataIndex);
					lines.push(`${item.marker}${item.seriesName}：${formatPrimaryPhaseMetricValue(rawValue, def?.metric ?? 'count')}`);
				}
				return lines.join('<br/>');
			},
			seriesDefs,
			formatSeriesValue: (def, rawValue) => formatPrimaryPhaseMetricValue(rawValue, def?.metric ?? 'count'),
		}),
		xAxis: horizontalBar ? valueAxes : categoryAxisConfig,
		yAxis: horizontalBar ? categoryAxisConfig : valueAxes.length === 1 ? valueAxes[0] : valueAxes,
		series: seriesDefs.map((def, index) => {
			const seriesIsBar = resolveSeriesIsBar(def.metric);
			const lineXAxisIndex = useSplitCategoryXAxis ? MIXED_COMPARE_LINE_X_AXIS_INDEX : 0;
			return {
				...buildMetricSeriesItem({
					name: def.name,
					values: def.values,
					yAxisIndex: horizontalBar ? 0 : def.yAxisIndex,
					xAxisIndex: horizontalBar ? def.yAxisIndex : seriesIsBar ? MIXED_COMPARE_BAR_X_AXIS_INDEX : lineXAxisIndex,
					chartMetric: def.metric,
					color: def.color,
					chartType,
					lineSmooth,
					showPointLabels,
					chartTheme,
					barBorderRadius,
					barLabelPosition,
					isBar: seriesIsBar,
					barMaxWidth: barStyle?.barMaxWidth,
				}),
				...(seriesIsBar
					? {
							...(horizontalBar
								? resolveHorizontalBarGapSeriesProps(seriesDefs, index, barStyle)
								: resolveVerticalCompareBarGapSeriesProps(index)),
							...resolveMixedCompareBarSeriesExtra(),
						}
					: useSplitCategoryXAxis
						? { z: MIXED_COMPARE_LINE_Z, zlevel: 1, triggerLineEvent: true }
						: null),
			};
		}),
	};
};
